/**
 * URL-driven filter state for mindspace surfaces.
 *
 * Step-3 of the Knowledge Archive build: extends the existing tag-only
 * model (`?domain=&user=&match=`) with the full filter set we need on
 * the archive graph - text search, source-lesson/document scoping,
 * and a time-window axis + bucket - while keeping everything in URL
 * params so a filtered view stays shareable / refreshable.
 *
 * Practice Arena and Quick Bytes keep using the original tag-only
 * subset; the additional fields are simply ignored there.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  HighlightsFeedFilters,
  KnowledgeGraphFilters,
  TagMatchMode,
  TimeAxis,
  TimeWindow,
} from '@/lib/api-client';

export type { TimeAxis, TimeWindow };

export interface TagFilterState {
  // Existing - used by all three surfaces.
  selectedDomain: string[];
  selectedUser: string[];
  match: TagMatchMode;
  isManual: boolean;
  // Step-3 additions - used by Knowledge Archive.
  q: string;
  selectedLessons: string[];
  selectedDocuments: string[];
  timeAxis: TimeAxis;
  window: TimeWindow;
  // FSRS intersect used by Quick Bytes and Knowledge Archive - toggled
  // from inside the TimeWindowFilter so it sits with the other temporal
  // filters.
  dueToday: boolean;
  // Quick Bytes only - intersect with the user's bookmarked concepts.
  bookmarked: boolean;
  // Knowledge Archive only - when on, paused (archived) concepts are shown
  // in the graph. Off by default so paused concepts stay out of the way;
  // needed so a user can find + resume a concept they previously paused.
  includeArchived: boolean;
  // Lets Knowledge Archive callers know whether any filter is active so
  // the UI can show a "Clear all" affordance.
  hasAnyFilter: boolean;
  setFilters: (next: Partial<FilterUpdate>) => void;
  /** Reset every filter to its default. Centralized so callers can't drift
   *  out of sync (and forget a newly-added filter like `includeArchived`). */
  clearAll: () => void;
  /** Reset the FILTERS - not the text search, not Quick Bytes' Bookmarks
   *  view. The phone sheet's "Clear all": the search box and the view tabs
   *  sit outside the sheet and say for themselves what they are set to. */
  clearFilters: () => void;
  /** Convert current state into the shape the API client wants. */
  asGraphFilters: () => KnowledgeGraphFilters;
  /** Convert current state into the shape the Quick Bytes highlights
   *  feed endpoint wants. Mirrors `asGraphFilters` but adds `due_today`
   *  and drops the source-document axis (highlights don't surface
   *  document attribution yet). */
  asHighlightsFilters: () => HighlightsFeedFilters;
}

export interface FilterUpdate {
  domain: string[];
  user: string[];
  match: TagMatchMode;
  q: string;
  lessons: string[];
  documents: string[];
  timeAxis: TimeAxis;
  window: TimeWindow;
  dueToday: boolean;
  bookmarked: boolean;
  includeArchived: boolean;
}

const DEFAULT_AXIS: TimeAxis = 'last_reviewed_at';
const DEFAULT_WINDOW: TimeWindow = 'all';

/**
 * How many filters are on, counting each picked lesson and tag separately.
 * The panel's "Clear N filters" and the phone's Filters badge both read it,
 * so the two cannot disagree. `search: false` leaves the text search out -
 * the phone badge sits beside the search box, which already shows it.
 */
export function countActiveFilters(
  s: TagFilterState,
  { search = true }: { search?: boolean } = {}
): number {
  return (
    (search && s.q ? 1 : 0) +
    s.selectedLessons.length +
    s.selectedDomain.length +
    s.selectedUser.length +
    (s.window !== DEFAULT_WINDOW ? 1 : 0) +
    (s.dueToday ? 1 : 0) +
    (s.includeArchived ? 1 : 0)
  );
}

export function useTagFilters(): TagFilterState {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedDomain = parseCsv(searchParams.get('domain'));
  const selectedUser = parseCsv(searchParams.get('user'));
  // Tag filtering is always 'any' (union) - the Any/All toggle was removed.
  // Pinned here so a stale `?match=all` URL can't resurrect intersection mode.
  const match: TagMatchMode = 'any';
  const q = searchParams.get('q')?.trim() ?? '';
  const selectedLessons = parseCsv(searchParams.get('lessons'), {
    lowercase: false,
  });
  const selectedDocuments = parseCsv(searchParams.get('documents'), {
    lowercase: false,
  });
  const timeAxis = (searchParams.get('axis') as TimeAxis) || DEFAULT_AXIS;
  const window = (searchParams.get('window') as TimeWindow) || DEFAULT_WINDOW;
  const dueToday = searchParams.get('due') === '1';
  const bookmarked = searchParams.get('bookmarked') === '1';
  const includeArchived = searchParams.get('archived') === '1';

  const isManual = selectedDomain.length > 0 || selectedUser.length > 0;
  const hasAnyFilter =
    isManual ||
    q.length > 0 ||
    selectedLessons.length > 0 ||
    selectedDocuments.length > 0 ||
    window !== DEFAULT_WINDOW ||
    dueToday ||
    bookmarked ||
    includeArchived;

  const setFilters = useCallback(
    (next: Partial<FilterUpdate>) => {
      const params = new URLSearchParams(searchParams);

      if (next.domain !== undefined) {
        next.domain.length
          ? params.set('domain', next.domain.join(','))
          : params.delete('domain');
      }
      if (next.user !== undefined) {
        next.user.length
          ? params.set('user', next.user.join(','))
          : params.delete('user');
      }
      if (next.match !== undefined) {
        next.match !== 'any'
          ? params.set('match', next.match)
          : params.delete('match');
      }
      if (next.q !== undefined) {
        next.q.trim() ? params.set('q', next.q.trim()) : params.delete('q');
      }
      if (next.lessons !== undefined) {
        next.lessons.length
          ? params.set('lessons', next.lessons.join(','))
          : params.delete('lessons');
      }
      if (next.documents !== undefined) {
        next.documents.length
          ? params.set('documents', next.documents.join(','))
          : params.delete('documents');
      }
      if (next.timeAxis !== undefined) {
        next.timeAxis !== DEFAULT_AXIS
          ? params.set('axis', next.timeAxis)
          : params.delete('axis');
      }
      if (next.window !== undefined) {
        next.window !== DEFAULT_WINDOW
          ? params.set('window', next.window)
          : params.delete('window');
      }
      if (next.dueToday !== undefined) {
        next.dueToday ? params.set('due', '1') : params.delete('due');
      }
      if (next.bookmarked !== undefined) {
        next.bookmarked
          ? params.set('bookmarked', '1')
          : params.delete('bookmarked');
      }
      if (next.includeArchived !== undefined) {
        next.includeArchived
          ? params.set('archived', '1')
          : params.delete('archived');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearAll = useCallback(() => {
    setFilters({
      domain: [],
      user: [],
      match: 'any',
      q: '',
      lessons: [],
      documents: [],
      timeAxis: DEFAULT_AXIS,
      window: DEFAULT_WINDOW,
      dueToday: false,
      bookmarked: false,
      includeArchived: false,
    });
  }, [setFilters]);

  const clearFilters = useCallback(() => {
    setFilters({
      domain: [],
      user: [],
      lessons: [],
      documents: [],
      timeAxis: DEFAULT_AXIS,
      window: DEFAULT_WINDOW,
      dueToday: false,
      includeArchived: false,
    });
  }, [setFilters]);

  const asGraphFilters = useCallback(
    (): KnowledgeGraphFilters => ({
      q: q || undefined,
      source_lesson_ids: selectedLessons.length ? selectedLessons : undefined,
      source_document_ids: selectedDocuments.length
        ? selectedDocuments
        : undefined,
      domain_tags: selectedDomain.length ? selectedDomain : undefined,
      user_tags: selectedUser.length ? selectedUser : undefined,
      match,
      time_axis: timeAxis,
      window,
      due_today: dueToday || undefined,
      // "Show paused" ADDS paused concepts to the map, drawn as hollow rings,
      // rather than swapping to a map of only them: on a map where every
      // concept is paused the ring explains nothing, and the student loses
      // the memory states they were reading.
      include_archived: includeArchived || undefined,
    }),
    [
      q,
      selectedLessons,
      selectedDocuments,
      selectedDomain,
      selectedUser,
      match,
      timeAxis,
      window,
      dueToday,
      includeArchived,
    ]
  );

  const asHighlightsFilters = useCallback(
    (): HighlightsFeedFilters => ({
      q: q || undefined,
      source_lesson_ids: selectedLessons.length ? selectedLessons : undefined,
      domain_tags: selectedDomain.length ? selectedDomain : undefined,
      user_tags: selectedUser.length ? selectedUser : undefined,
      match,
      // Highlights default to `created_at` because most concepts in the
      // feed haven't been reviewed yet - `last_reviewed_at` would hide
      // freshly-generated cards.
      time_axis: 'created_at',
      window,
      due_today: dueToday || undefined,
      bookmarked: bookmarked || undefined,
    }),
    [
      q,
      selectedLessons,
      selectedDomain,
      selectedUser,
      match,
      window,
      dueToday,
      bookmarked,
    ]
  );

  return {
    selectedDomain,
    selectedUser,
    match,
    isManual,
    q,
    selectedLessons,
    selectedDocuments,
    timeAxis,
    window,
    dueToday,
    bookmarked,
    includeArchived,
    hasAnyFilter,
    setFilters,
    clearFilters,
    clearAll,
    asGraphFilters,
    asHighlightsFilters,
  };
}

/**
 * Aggregate every tag we've seen across the user's queue/graph concepts
 * as filter options, sorted by frequency. Cheap; works for both queue
 * concepts and graph nodes since both expose `domain_tags`/`user_tags`.
 */
export function useTagOptions(
  concepts:
    | { domain_tags?: string[] | null; user_tags?: string[] | null }[]
    | undefined
) {
  return useMemo(() => buildTagOptions(concepts ?? []), [concepts]);
}

function buildTagOptions(
  concepts: {
    domain_tags?: string[] | null;
    user_tags?: string[] | null;
  }[]
) {
  const domain = new Map<string, number>();
  const user = new Map<string, number>();
  for (const c of concepts) {
    for (const t of c.domain_tags ?? []) {
      domain.set(t, (domain.get(t) || 0) + 1);
    }
    for (const t of c.user_tags ?? []) {
      user.set(t, (user.get(t) || 0) + 1);
    }
  }
  return {
    domainOptions: Array.from(domain.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t),
    userOptions: Array.from(user.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t),
  };
}

function parseCsv(
  value: string | null,
  opts: { lowercase?: boolean } = { lowercase: true }
): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map(t => (opts.lowercase ? t.trim().toLowerCase() : t.trim()))
    .filter(Boolean);
}
